import React, { useEffect, useState } from "react";
import useEditLoad from "../../hooks/useEditLoad";
import EditLoadGate from "../../components/editLoadGate";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Select from "../../components/select";
import { AsyncSelect } from "../../components/select";
import { BsFiletypeCsv } from "react-icons/bs";

import noImage from '../../images/no-image.png';
import { uploadMediaFile } from "../../utills/fileUpload";
import { useLanguage } from "../../context/languageContext";
import PreviewImage from "../../components/image/previewImage";
import BulkUploadCollection from "./bulkUpload";
import Button from "../../components/button";

import LanguageSelector from "../../components/languageSelector";
import { fetchAllBookCategoriesByLangOptions, fetchAllGenresByLangOptions, normalizeCoverImage , pastePlainText } from "../../helper";
import SortablePeopleList from "../../components/sortablePeople";
import { usePermissions } from "../../context/permissionContext";
import { useAlreadyExists } from "../../components/confirmDialog";
import { rankMatches } from "../../helper/searchFold";


const AddEditPopup = (props) => {
    const { submitLabel } = usePermissions();
    const { languages, selectedLang, setSelectedLang, adminInfo, initialLangRef, fallbackLang } = useLanguage();
    const showAlreadyExists = useAlreadyExists();
    const { t } = useTranslation();

    const {
        isAddCollectionModalVisible,
        setIsAddCollectionModalVisible,
        isEditCollectionModalVisible,
        handleCloseModal,
        collectionId,
        onSuccess,
        // Renders the same form full width on its own route instead of in a
        // dialog — a grid of covers needs the window.
        asPage = false,
    } = props;

    const createInitialLangValues = (lang) => ({
        langCode: lang,
        title: "",
        description: "",
        books: [],
        genres: [],
    });

    const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

    const [values, setValues] = useState({
        coverImage: {},
        translations: createInitialTranslationsArr(),
    });

    const [isBulkUploadCollectionPopupVisible, setIsBulkUploadCollectionPopupVisible] = useState(false);

    const [particularCollectionsData, setParticularCollectionsData] = useState(null);
    const [selectedBooks, setSelectedBooks] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]);

    const [editMediaError, setEditMediaError] = useState("");
    const [errors, setErrors] = useState({});
    const [isSubmit, setIsSubmit] = useState(false);
    // Edit shows, and saves, only the journey that was opened — see hooks/useEditLoad.
    const load = useEditLoad();
    const editReady = !isEditCollectionModalVisible || load.isLoadedFor(collectionId);

    useEffect(() => {
        if (isEditCollectionModalVisible && collectionId) {
            load.begin(collectionId);
            initialLangRef.current = false;
            handleFetchParticularCollection(String(collectionId));
        }
        if (!isEditCollectionModalVisible) load.reset();
    }, [isEditCollectionModalVisible, collectionId]);

    useEffect(() => {
        if ((isAddCollectionModalVisible || isEditCollectionModalVisible) && fallbackLang) {
            setSelectedLang(fallbackLang);
        }
    }, [isAddCollectionModalVisible, isEditCollectionModalVisible]);

    useEffect(() => {
        if (isAddCollectionModalVisible) {
            fetchAllBooksData();
        }
    }, [isAddCollectionModalVisible]);

    const handleFetchParticularCollection = async (id) => {
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/collection/get/particular/${id}`, { headers });

            if (response?.status && load.accepts(id, response.data)) {
                setParticularCollectionsData(response.data);
                await fetchAllBooksData();
            } else {
                load.fail(id, response?.message || "");
            }
        } catch (error) {
            console.error("handleFetchParticularCollection error:", error);
            load.fail(id, error);
        }
    };

    useEffect(() => {
        if (particularCollectionsData && particularCollectionsData.translations && !initialLangRef.current) {
            const mergedTranslations = languages.map((lang) => {
                return (
                    particularCollectionsData.translations.find((t) => t.langCode === lang) || {
                        langCode: lang,
                        title: particularCollectionsData?.title || "",
                        description: particularCollectionsData?.description || "",
                        books: particularCollectionsData?.books || [],
                        genres: particularCollectionsData?.genres || [],
                    }
                );
            });

            setValues((prevValues) => ({
                ...prevValues,
                coverImage: normalizeCoverImage(particularCollectionsData.coverImage),
                translations: mergedTranslations,
            }));

            initialLangRef.current = true;
            load.loaded(particularCollectionsData);
        }
    }, [isEditCollectionModalVisible, particularCollectionsData, languages]);

    const handleUploadMedia = async (event) => {
        const result = await uploadMediaFile(event, true);

        if (typeof result === "string") {
            setEditMediaError(result);
        } else if (typeof result === "object" && result.length > 0) {
            setValues((prevValues) => ({
                ...prevValues,
                coverImage: result[0]?.data,
            }));
        }
    };

    const handleUpdateTranslationField = async (fieldName, value, langCode) => {
        setValues((prevValues) => {
            const updatedTranslations = prevValues.translations?.map((translation) =>
                translation.langCode === langCode
                    ? {
                        ...translation,
                        [fieldName]: value,
                    }
                    : translation
            );

            return {
                ...prevValues,
                translations: updatedTranslations,
            };
        });

        await handleUpdateGenresByBooks(value, langCode);

    };

    const handleSelectLanguage = (language) => {
        setSelectedLang(language);

        setValues((prevValues) => {
            const isLanguagesExists = prevValues.translations.some((lang) => lang.langCode === language);

            if (isLanguagesExists) {
                return { ...prevValues };
            }

            return {
                ...prevValues,
                translations: [...prevValues.translations, createInitialLangValues(language)],
            };
        });
    };

    const handleGetTranslationByLang = (langCode) => {
        return values.translations?.find((lang) => lang.langCode === langCode) || {};
    };

    const fetchAllBooksData = async () => {
        // Only books with a published summary: a collection tile opens the
        // summary reader, so a book without one would be a dead tile.
        const booksOptions = await fetchAllBookCategoriesByLangOptions(selectedLang, adminInfo.token, true);
        const genresOptions = await fetchAllGenresByLangOptions(selectedLang, adminInfo.token);

        const booksData = booksOptions.map((book) => ({
            label: book.bookName,
            value: book.bookId,
            authorName: book.authorName,
            image: book.image,
        }));
        const genresData = genresOptions.map((genre) => ({
            label: genre.genreName,
            value: genre.genreId,
            type: genre.type,
        }));

        setSelectedBooks(booksData);
        setSelectedGenres(genresData);
    };

    // Books as options for both the picker and the grid, in stored order. The
    // cover comes from the full book list loaded once above; the collection
    // itself only stores id and name.
    const bookOptionsForLang = (handleGetTranslationByLang(selectedLang)?.books || []).map((book) => {
        const known = selectedBooks.find((b) => b.value === book.bookId);
        return {
            value: book.bookId,
            label: known?.authorName ? `${book.bookName} — ${known.authorName}` : book.bookName,
            image: known?.image,
        };
    });

    // A book just picked goes to the front: appending it to a shelf of a
    // hundred drops it below the fold, so nothing appears to happen.
    const handleBooksPicked = (options) => {
        const current = handleGetTranslationByLang(selectedLang)?.books || [];
        const currentIds = new Set(current.map((b) => b.bookId));
        const incoming = (options || []).map((o) => ({ bookId: o.value, bookName: o.label }));
        const incomingIds = new Set(incoming.map((b) => b.bookId));

        const added = incoming.filter((b) => !currentIds.has(b.bookId));
        const kept = current.filter((b) => incomingIds.has(b.bookId));
        handleUpdateTranslationField("books", [...added, ...kept], selectedLang);
    };

    // The grid hands back the exact order it wants — from a drag, the position
    // box or the move buttons — so it is taken literally. Reordering the stored
    // books by id keeps the stored bookName rather than the option's label,
    // which has the author appended for the picker's benefit.
    const handleBooksReordered = (options) => {
        const current = handleGetTranslationByLang(selectedLang)?.books || [];
        const byId = new Map(current.map((b) => [b.bookId, b]));
        const next = (options || []).map((o) => byId.get(o.value)).filter(Boolean);
        handleUpdateTranslationField("books", next, selectedLang);
    };

    const handleUpdateGenresByBooks = async (value, language) => {
        try {
            const headers = { Authorization: adminInfo.token ? `Bearer ${adminInfo.token}` : null };
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/genres-by-books`, { value, language }, { headers });

            if (response.status) {
                const allGenresByBooks = response?.data || [];
                const genresByBooks = allGenresByBooks.map((genre) => ({
                    genreName: genre.genreName,
                    genreId: genre.genreId,
                    type: genre.type,
                }));

                setValues((prevValues) => {
                    const updatedTranslations = prevValues.translations.map((translation) => {
                        if (translation.langCode === language) {
                            return {
                                ...translation,
                                genres: genresByBooks,
                            }
                        }
                        return translation;
                    });

                    return {
                        ...prevValues,
                        translations: updatedTranslations,
                    };
                });
            }
        } catch (error) {
            console.error("handleUpdateGenresBySelectedBooks catch error...", error)
        }
    };

    const handleClose = () => {
        handleCloseModal();
        setErrors({});
        setEditMediaError("");
        setParticularCollectionsData(null);
        setValues({
            coverImage: {},
            translations: createInitialTranslationsArr()
        });
    };

    const validateFields = (value) => {
        let newErrors = {};
        const translation = value?.translations?.find(t => t.langCode === selectedLang);

        if (!translation.title || translation.title.trim() === "") {
            newErrors.title = "Please enter a name for the collection";
        }

        if (!translation.description || translation.description.trim() === "") {
            newErrors.description = "Please provide the description of the collection";
        }

        if (!translation.books || translation.books.length === 0) {
            newErrors.books = "Please add book name";
        }

        if (!translation.genres || translation.genres.length === 0) {
            newErrors.genres = "Please add genre";
        }

        // if (!value.coverImage?.uploadedImageUrl) {
        //     newErrors.coverImage = "Please upload an image on the cover";
        // }

        return newErrors;
    };

    const handleAddAndUpdateValues = async () => {
        // Never save an edit form that is not showing this journey.
        if (!editReady) return;
        const newErrors = validateFields(values);

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const url = isEditCollectionModalVisible
                ? `${process.env.REACT_APP_API_ROOT}/api/admin/collection/update/${collectionId}`
                : `${process.env.REACT_APP_API_ROOT}/api/admin/collection/add`;

            const method = isEditCollectionModalVisible ? "put" : "post";
            const { data: response } = await axios[method](url, { values }, { headers });

            if (response.status) {
                setErrors({});
                onSuccess();
                handleCloseModal();
                setParticularCollectionsData(null);
                setValues({
                    coverImage: {},
                    translations: createInitialTranslationsArr()
                });
                toast.success(response?.message);
                setIsSubmit(false);
            }
        } catch (error) {
            console.error("handleAddAndUpdateValues catch error::", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error;
            if (!showAlreadyExists(error)) toast.error(errorMessage);
            setIsSubmit(false);
        } finally {
            setIsSubmit(false);
        }
    };

    const handleShowBulkUploadCollectionPopup = () => {
        setIsBulkUploadCollectionPopupVisible(true);
        setIsAddCollectionModalVisible(false);
    };

    const handleCloseBulkUploadCollectionPopup = () => {
        setIsBulkUploadCollectionPopupVisible(false);
    };

    const BookOption = (props) => {
        const { data, innerRef, innerProps, className, isFocused, isSelected } = props;
    
        const backgroundColor = isSelected ? "var(--ps-green)" : isFocused ? "var(--ps-surface-2)" : "transparent";
        const borderColor = isSelected ? "var(--ps-green)" : isFocused ? "var(--ps-border)" : "transparent";
        const textColor = isSelected ? "var(--ps-green-ink)" : "var(--ps-text)";
    
        return (
          <div
            ref={innerRef}
            {...innerProps}
            className={`d-flex align-items-center gap-3 py-2 px-3 ${className || ''}`}
            style={{
              cursor: 'pointer',
              backgroundColor,
              borderRadius: 8,
              border: `1px solid ${borderColor}`,
              transition: 'background-color 0.15s ease, border-color 0.15s ease',
              overflow: 'hidden',
            }}
          >
            <div
              className="flex-shrink-0 rounded-circle overflow-hidden"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: "1px solid var(--ps-border)",
                backgroundColor: "var(--ps-surface-2)",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={data.image || noImage}
                alt={data.label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="flex-grow-1 min-w-0">
              <div className="fw-medium text-truncate" style={{ color: textColor }}>
                {data.label}
              </div>
              {data.authorName && (
                <div className="small text-truncate" style={{ color: isSelected ? "rgba(13, 13, 13, 0.72)" : "var(--ps-text-muted)" }}>
                  {data.authorName}
                </div>
              )}
            </div>
          </div>
        );
      };


    // Extracted so the same form can render either in a dialog or full width
    // on its own route. A grid of covers needs the whole window.
    const body = (
                <div className="box_wrapper">
                    <div className="d-flex justify-content-between align-items-center fw-bold gap-2 mb-3">
                        <h4 className="text-center">{isEditCollectionModalVisible ? "Edit Collection" : " Add Collection"}</h4>
                        {isAddCollectionModalVisible && <button type="button" className="btn btn-outline-dark" onClick={handleShowBulkUploadCollectionPopup}>Add Collections in Bulk through CSV <BsFiletypeCsv /></button>}
                        {isEditCollectionModalVisible && <LanguageSelector onSelectLanguage={handleSelectLanguage} />}
                    </div>
                    <EditLoadGate ready={editReady} error={load.loadError}>
                    <div className="modal_content_lg">
                        <div className="row my-2">
                            <div className="col-sm-12 mb-3">
                                <label className="label">{t("collections.collectionName")}:</label>
                                <input
                                    className="form_control"
                                    placeholder={`${t("general.enter")} ${t("collections.collectionName")}...`}
                                    type="text"
                                    name="title"
                                    value={handleGetTranslationByLang(selectedLang).title}
                                    onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                                />
                                {errors && errors.title && <p className="error-message d-block">{errors.title}</p>}
                            </div>
                            <div className="col-sm-12 mb-3">
                                <label className="label">{t("collections.selectBooks")}:</label>
                                {/* The picker only adds. The grid below owns order and removal, so the
                                    picker's own chips are hidden — two places to remove a book from is
                                    one too many. */}
                                <AsyncSelect
                                    isMulti
                                    markAdded
                                    controlShouldRenderValue={false}
                                    closeMenuOnSelect={false}
                                    placeholder={`${t("general.enter")} ${t("collections.selectBooks")}...`}
                                    name="books"
                                    value={bookOptionsForLang}
                                    loadOptions={(inputValue, callback) => {
                                        setTimeout(() => {
                                            // Best match first: exact title, starts with, word starts with, the rest.
                                            callback(rankMatches(selectedBooks, inputValue || "").slice(0, 100));
                                        }, 100);
                                    }}
                                    defaultOptions={selectedBooks.slice(0, 100)}
                                    components={{ Option: BookOption }}
                                    onChange={handleBooksPicked}
                                    cacheOptions
                                    isSearchable={true}
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue ? `No books found for "${inputValue}"` : "Start typing to search books..."
                                    }
                                />
                                <div className="mt-2">
                                    <SortablePeopleList
                                        items={bookOptionsForLang}
                                        onChange={handleBooksReordered}
                                        thumbShape="cover"
                                        variant="grid"
                                        emptyMessage="No books yet — search above to add some."
                                    />
                                </div>
                                <small className="text-muted">
                                    This is the order they appear in the app. Drag to reorder, or type a
                                    position to move a book straight there.
                                </small>
                                {errors && errors.books && <p className="error-message d-block">{errors.books}</p>}
                            </div>
                            <div className="col-sm-12 col-md-6 mb-3">
                                <label className="label">{t("general.genre")}:</label>
                                <Select
                                    isMulti
                                    placeholder={`${t("general.enter")} ${t("general.genre")}...`}
                                    name="genre"
                                    value={handleGetTranslationByLang(selectedLang)?.genres?.map((option) => ({ label: option.genreName, value: option.genreId })) || []}
                                    options={selectedGenres?.filter((genre) => genre.type === handleGetTranslationByLang(selectedLang)?.type) || []}
                                    onChange={(selectedOptions) => {
                                        const updatedGenre = selectedOptions.map((option) => ({ genreName: option.label, genreId: option.value }));
                                        handleUpdateTranslationField("genres", updatedGenre, selectedLang);
                                    }}
                                    isDisabled={true}
                                />
                                {errors && errors.genres && <p className="error-message d-block">{errors.genres}</p>}
                            </div>
                            <div className="col-sm-12 col-md-8 mb-3">
                                <label className="label">{t("collections.description")}:</label>
                                <textarea
                                    className="form_control"
                                    placeholder={`${t("general.enter")} ${t("collections.description")}...`}
                                    type="text"
                                    name="description"
                                    value={handleGetTranslationByLang(selectedLang).description}
                                    onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                                    // Pasted markdown markers ("**The Internet of Money**") are stripped:
                                    // this field stores plain text, so they would be saved and shown literally.
                                    onPaste={(event) =>
                                      pastePlainText(event, (value) =>
                                        handleUpdateTranslationField("description", value, selectedLang)
                                      )
                                    }
                                    style={{ height: "330px" }}
                                />
                                {errors && errors.description && <p className="error-message d-block">{errors.description}</p>}
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <PreviewImage
                                    label={t("general.coverImage")}
                                    uploadMedia={(e) => handleUploadMedia(e)}
                                    image={values.coverImage?.uploadedImageUrl}
                                    mediaError={editMediaError}
                                    error={errors?.coverImage}
                                />
                            </div>
                        </div>
                    </div>
                    </EditLoadGate>
    
                    <div className="d-flex gap-2 justify-content-end mt-3">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
                        <Button onClick={handleAddAndUpdateValues} disabled={isSubmit || !editReady} isSubmit={isSubmit} label="Submitting ..." text={isEditCollectionModalVisible ? "Update" : submitLabel("collections")} />
                    </div>
                </div>
    );

    return (
        <React.Fragment>
            {asPage ? (
                <div className="px-4 py-4">{body}</div>
            ) : (
                <Modal
                    isOpen={isAddCollectionModalVisible || isEditCollectionModalVisible}
                    onRequestClose={handleCloseModal}
                    className="modal_body_md"
                    overlayClassName="modal_overlay"
                >
                    {body}
                </Modal>
            )}

            {isBulkUploadCollectionPopupVisible && (
                <BulkUploadCollection
                    isPopupVisible={isBulkUploadCollectionPopupVisible}
                    handleClosePopup={handleCloseBulkUploadCollectionPopup}
                    onSuccess={onSuccess}
                />
            )}
        </React.Fragment>
    );
};

export default AddEditPopup;
